import { useRef, useEffect } from "react";

const STAR_COUNT = 1000;

function randomStar(centerX: number, centerY: number, maxR: number) {
  const angle = Math.random() * 2 * Math.PI;
  const r = 40 + Math.random() * (maxR - 60);
  return {
    angle,
    r,
    size: 1.2 + Math.random() * 1.8,
    color: Math.random() < 0.95 ? "#fff" : "#ffe09d",
  };
}

interface WarpJumpProps {
  active: boolean;
  duration?: number;
  onEnd?: () => void;
  nextLevelText?: string;
}

export function WarpJump({ active, duration = 2500, onEnd, nextLevelText }: WarpJumpProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const raf = useRef<number>();

  useEffect(() => {
    if (!active) return;
    const w = window.innerWidth, h = window.innerHeight;
    const cx = w / 2, cy = h / 2;
    const ctx = canvasRef.current!.getContext("2d")!;
    canvasRef.current!.width = w;
    canvasRef.current!.height = h;
    const stars = Array.from({ length: STAR_COUNT }, () => randomStar(cx, cy, Math.max(w, h) * 1.1));
    let start: number | null = null;

    function easeInOut(t: number) {
      return t < 0.65
        ? 0.4 * Math.pow(t / 0.65, 1.25)
        : 0.4 + 0.6 * Math.pow((t - 0.65) / 0.35, 2.7);
    }

    function animate(ts: number) {
      if (!start) start = ts;
      let rawProgress = Math.min((ts - start) / duration, 1);
      let progress = easeInOut(rawProgress);

      ctx.clearRect(0, 0, w, h);

      if (rawProgress > 0.80) {
        ctx.save();
        ctx.globalAlpha = Math.max(0, 0.32 + 0.6 * (rawProgress - 0.80));
        ctx.beginPath();
        ctx.arc(cx, cy, 150 + 2400 * Math.pow(rawProgress - 0.80, 2.4), 0, Math.PI * 2);
        ctx.fillStyle = "#fff";
        ctx.filter = "blur(16px)";
        ctx.fill();
        ctx.restore();
      }

      for (let star of stars) {
        let stretch = rawProgress > 0.83
          ? 1 + 8 * Math.pow((rawProgress - 0.83) / 0.17, 2.3)
          : 1;

        const r = 30 + (star.r * progress * stretch);
        const x1 = cx + Math.cos(star.angle) * r;
        const y1 = cy + Math.sin(star.angle) * r;
        const x0 = cx + Math.cos(star.angle) * (r * (0.78 - 0.4 * progress));
        const y0 = cy + Math.sin(star.angle) * (r * (0.78 - 0.4 * progress));

        ctx.save();
        ctx.strokeStyle = star.color;
        ctx.globalAlpha = Math.max(0.13, 1 - progress * 1.1);
        ctx.lineWidth = star.size + progress * 3.2 + (rawProgress > 0.87 ? 7 * (rawProgress - 0.87) : 0);
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
        ctx.restore();
      }

      if (rawProgress < 1) {
        raf.current = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, w, h);
        onEnd && onEnd();
      }
    }
    raf.current = requestAnimationFrame(animate);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [active, duration, onEnd]);

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          display: active ? "block" : "none",
          position: "fixed",
          inset: 0,
          zIndex: 9000,
          pointerEvents: "none",
          background: "rgba(0,0,0,0.11)",
        }}
      />
      {active && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9002,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div className="warp-text">{nextLevelText}</div>
        </div>
      )}
    </>
  );
}
